import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Trash2, Target, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  TreatmentGoal,
  GoalStatus,
  VitalType,
  TargetDirection,
  getGoalStatusColor,
  getGoalStatusLabel,
  getVitalTypeLabel,
  getVitalTypeUnit,
  getTargetDirectionSymbol,
  getTargetDirectionLabel,
} from "@/lib/treatmentPlan/treatmentPlanData";
import { Check, X } from "lucide-react";

interface GoalsSectionProps {
  goals: TreatmentGoal[];
  addGoal: (goal: TreatmentGoal) => void;
  updateGoal: (id: string, updates: Partial<TreatmentGoal>) => void;
  removeGoal: (id: string) => void;
  isDarkMode: boolean;
  isAdding?: boolean;
  setIsAdding?: (isAdding: boolean) => void;
  pendingGoal?: { description: string; linkedVitalType?: any; notes?: string };
  consultationStatus?: number;
  editMode?: boolean;
  stateConsultationId?: string;
}

export const GoalsSection: React.FC<GoalsSectionProps> = ({
  goals,
  addGoal,
  updateGoal,
  removeGoal,
  isDarkMode,
  isAdding = false,
  setIsAdding = () => {},
  pendingGoal,
  consultationStatus,
  editMode = false,
  stateConsultationId,
}) => {
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [newGoal, setNewGoal] = useState({
    description: "",
    linkedVitalType: undefined as VitalType | undefined,
    targetDirection: "AtOrBelow" as TargetDirection,
    targetValue: "",
    targetDate: "",
    status: "pending" as GoalStatus,
    notes: "",
  });
  useEffect(() => {
    if (isAdding && pendingGoal?.description) {
      setNewGoal((prev) => ({
        ...prev,
        description: pendingGoal.description,
        linkedVitalType: pendingGoal.linkedVitalType ?? prev.linkedVitalType,
        notes: pendingGoal.notes || "",
      }));
    }
  }, [isAdding, pendingGoal]);

  const handleAddGoal = () => {
    if (newGoal.description && newGoal.targetValue && newGoal.targetDate) {
      const goal: TreatmentGoal = {
        id: uuidv4(),
        ...newGoal,
        createdAt: new Date().toISOString(),
      };
      addGoal(goal);
      setNewGoal({
        description: "",
        linkedVitalType: undefined,
        targetDirection: "AtOrBelow" as TargetDirection,
        targetValue: "",
        targetDate: "",
        status: "pending",
        notes: "",
      });
      setIsAdding(false);
    }
  };

  const handleCancelAdd = () => {
    setNewGoal({
      description: "",
      linkedVitalType: undefined,
      targetDirection: "AtOrBelow" as TargetDirection,
      targetValue: "",
      targetDate: "",
      status: "pending",
      notes: "",
    });
    setIsAdding(false);
  };

  return (
    <div className="space-y-4">
      {/* Add New Goal Form */}
      {isAdding && (
        <Card
          className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <CardHeader>
            <CardTitle
              className={`text-base ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
            >
              New Goal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="new-goal-description"
                className={isDarkMode ? "text-gray-300" : "text-gray-700"}
              >
                Goal Description *
              </Label>
              <Textarea
                id="new-goal-description"
                placeholder="e.g., Reduce weight by 10kg, Lower blood pressure to 120/80"
                value={newGoal.description}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, description: e.target.value })
                }
                className={`${
                  isDarkMode
                    ? "bg-gray-900 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300"
                }`}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="new-goal-vital-type"
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Linked Vital (optional)
                </Label>
                <Select
                  value={newGoal.linkedVitalType}
                  onValueChange={(value: VitalType) =>
                    setNewGoal({ ...newGoal, linkedVitalType: value })
                  }
                >
                  <SelectTrigger
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : "bg-white border-gray-300"
                    }
                  >
                    <SelectValue placeholder="Select vital type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weight">Weight</SelectItem>
                    <SelectItem value="blood-pressure">
                      Blood Pressure
                    </SelectItem>
                    <SelectItem value="heart-rate">Heart Rate</SelectItem>
                    <SelectItem value="temperature">Temperature</SelectItem>
                    <SelectItem value="height">Height</SelectItem>
                    <SelectItem value="bmi">BMI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ← NEW DROPDOWN */}
              <div className="space-y-2">
                <Label
                  htmlFor="new-goal-target-direction"
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Target Should Be
                </Label>
                <Select
                  value={newGoal.targetDirection}
                  onValueChange={(value: TargetDirection) =>
                    setNewGoal({ ...newGoal, targetDirection: value })
                  }
                >
                  <SelectTrigger
                    id="new-goal-target-direction"
                    className={
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : "bg-white border-gray-300"
                    }
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AtOrBelow">At or below (≤)</SelectItem>
                    <SelectItem value="AtOrAbove">At or above (≥)</SelectItem>
                    <SelectItem value="EqualTo">Equal to (=)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="new-goal-target-value"
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Target Value *
                  {newGoal.linkedVitalType && (
                    <span className="text-xs ml-1 text-gray-500">
                      ({getVitalTypeUnit(newGoal.linkedVitalType)})
                    </span>
                  )}
                </Label>
                <Input
                  id="new-goal-target-value"
                  type="text"
                  placeholder={
                    newGoal.linkedVitalType
                      ? `e.g., ${newGoal.linkedVitalType === "blood-pressure" ? "120/80" : "70"}`
                      : "e.g., 10kg, 120/80 mmHg"
                  }
                  value={newGoal.targetValue}
                  onChange={(e) =>
                    setNewGoal({ ...newGoal, targetValue: e.target.value })
                  }
                  className={
                    isDarkMode
                      ? "bg-gray-900 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300"
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="new-goal-target-date"
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Target Date *
                </Label>
                <Input
                  id="new-goal-target-date"
                  type="date"
                  value={newGoal.targetDate}
                  onChange={(e) =>
                    setNewGoal({ ...newGoal, targetDate: e.target.value })
                  }
                  className={`${
                    isDarkMode
                      ? "bg-gray-900 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="new-goal-status"
                  className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                >
                  Status
                </Label>
                <Select
                  value={newGoal.status}
                  onValueChange={(value: GoalStatus) =>
                    setNewGoal({ ...newGoal, status: value })
                  }
                >
                  <SelectTrigger
                    className={`${
                      isDarkMode
                        ? "bg-gray-900 border-gray-600 text-gray-200"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="achieved">Achieved</SelectItem>
                    <SelectItem value="abandoned">Abandoned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="new-goal-notes"
                className={isDarkMode ? "text-gray-300" : "text-gray-700"}
              >
                Notes (optional)
              </Label>
              <Textarea
                id="new-goal-notes"
                placeholder="Additional notes or context for this goal"
                value={newGoal.notes}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, notes: e.target.value })
                }
                className={`${
                  isDarkMode
                    ? "bg-gray-900 border-gray-600 text-gray-200"
                    : "bg-white border-gray-300"
                }`}
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleCancelAdd}
                className={
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : ""
                }
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleAddGoal}
                disabled={
                  !newGoal.description ||
                  !newGoal.targetValue ||
                  !newGoal.targetDate
                }
                className="bg-black hover:bg-gray-900 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Add Goal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Goals List */}
      {goals.length === 0 && !isAdding ? (
        <Card
          className={`${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}
        >
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target
              className={`w-12 h-12 mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
            />
            <p
              className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              No goals added yet
            </p>
            <p
              className={`text-sm text-center mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
            >
              Click "Add" to create your first treatment goal
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => (
            <Card
              key={goal.id}
              className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={getGoalStatusColor(goal.status)}>
                        {getGoalStatusLabel(goal.status)}
                      </Badge>
                      {goal.linkedVitalType && (
                        <Badge
                          variant="outline"
                          className={`${
                            isDarkMode
                              ? "bg-gray-700 text-gray-300 border-gray-600"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                          }`}
                        >
                          <Activity className="w-3 h-3 mr-1" />
                          {getVitalTypeLabel(goal.linkedVitalType)}
                        </Badge>
                      )}
                      <span
                        className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Target: {new Date(goal.targetDate).toLocaleDateString()}
                      </span>
                    </div>

                    <p
                      className={`text-sm font-medium mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                    >
                      {goal.description}
                    </p>

                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Target Value:
                      </span>
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                      >
                        {/* ← ADD the symbol prefix */}
                        {goal.targetDirection && (
                          <span
                            className="mr-1"
                            title={getTargetDirectionLabel(
                              goal.targetDirection,
                            )}
                          >
                            {getTargetDirectionSymbol(goal.targetDirection)}
                          </span>
                        )}
                        {goal.targetValue}
                        {goal.linkedVitalType && (
                          <span className="text-xs ml-1 text-gray-500">
                            {getVitalTypeUnit(goal.linkedVitalType)}
                          </span>
                        )}
                      </span>
                    </div>

                    {goal.notes && (
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {goal.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={editMode && stateConsultationId && consultationStatus === 1}
                          className={
                            isDarkMode
                              ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                              : "text-gray-600 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Goal</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this goal? This
                            action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeGoal(goal.id)}
                            className="bg-gray-900 hover:bg-gray-800"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
      >
        {goals.length > 0 && (
          <p>
            {goals.filter((g) => g.status === "achieved").length} of{" "}
            {goals.length} goals achieved
          </p>
        )}
      </div>
    </div>
  );
};
